import { randomHex } from '@authe/fe-commons/ids';
import { formatRelativeTime } from '@authe/fe-commons/time';
import * as React2 from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { unwrapData, ApiError, listOutcome } from '@authe/fe-commons/api';
import { createLogger } from '@authe/fe-commons/logging';
import { createScopedStorage } from '@authe/fe-commons/storage';
import { notifySuccess, notifyError, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DropdownMenu, DropdownMenuTrigger, Button, cn, DropdownMenuContent, SearchIcon, Input, DropdownMenuLabel, DropdownMenuItem, CheckIcon, DropdownMenuSeparator, Skeleton, ErrorState, EmptyState, Badge, PencilIcon, StarIcon, ShareIcon, TrashIcon, useNow, Alert, AlertTitle, AlertDescription, Label, NativeSelect, ChainSelect, Textarea, Field, Select, FormControl, SelectTrigger, SelectValue, SelectContent, SelectItem, DialogFooter, writeClipboardText, notifyInfo, useOfferedRungs, notifyWarning } from '@authe/ui-core';
import { useResourceRevision } from '@authe/ui-core/api';
import { offeredOptions } from '@authe/fe-commons/tenancy';

// src/config.ts
var SAVED_VIEWS_PACKAGE = "saved-views";
var SV_ACTIVE_SUFFIX = "active";
var SV_DRAFT_SUFFIX = "draft";
var SV_URL_PARAM = "view";
var SV_MAX_NAME_LENGTH = 80;
var SV_MAX_SHARE_URL_LENGTH = 2048;
var SAVED_VIEW_ACCESS_RESOURCE = "saved-view-access";
var SV_OPENS_REFRESH_MS = 3e4;
var VIEW_SCOPES = ["user", "tenant"];
var SCOPE_LABELS = {
  user: "Personal",
  team: "Team",
  tenant: "Shared",
  org: "Organization",
  customer: "Customer",
  system: "Default"
};
function isViewScope(value) {
  return typeof value === "string" && ["user", "team", "tenant", "org", "customer", "system"].includes(value);
}
function newViewId() {
  return `sv_${Date.now().toString(36)}${randomHex(8)}`;
}
function emptyView(tableId, scope = "user") {
  const now = Date.now();
  return {
    id: newViewId(),
    name: "",
    tableId,
    scope,
    state: {},
    createdAt: now,
    updatedAt: now
  };
}
function isEmptyState(state) {
  return !state.columns?.length && !state.filters?.length && !state.sort?.length && !state.pinnedRows?.length && !state.density && !state.extra;
}
var SHARE_TARGETS = ["public", "user", "team", "tenant", "org"];
var SHARE_TARGET_RUNG = {
  public: null,
  user: null,
  team: null,
  tenant: "tenant",
  org: "org"
};
var SHARE_TARGET_LABELS = {
  public: "Anyone with the link",
  user: "Specific people",
  team: "A team",
  tenant: "Everyone in this tenant",
  org: "Everyone in this organization"
};
var VIEW_DATA_MODES = ["static", "dynamic"];
var VIEW_DATA_MODE_LABELS = {
  static: "Snapshot (frozen at share time)",
  dynamic: "Live (updates after sharing)"
};
function stampOf(ms) {
  try {
    const out = new Date(ms).toLocaleString(void 0, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short"
    });
    return out && !out.toLowerCase().includes("invalid") ? out : null;
  } catch {
    return null;
  }
}
function describeOpens(openCount, lastOpenedAt, nowMs = Date.now()) {
  const count = openCount ?? 0;
  if (count <= 0) return null;
  const times = count === 1 ? "once" : `${count} times`;
  const last = lastOpenedAt ? ` \xB7 last ${formatRelativeTime(lastOpenedAt, { unit: "milliseconds", nowMs })}` : "";
  return `Opened ${times} via its links${last}`;
}
function describeShareSubtitle(input) {
  const { dataMode, frozenAt, live } = input;
  if (live) {
    const stamp = frozenAt ? stampOf(frozenAt) : null;
    return stamp ? `Kept up to date \xB7 updated ${stamp}` : "Kept up to date by its owner";
  }
  if (frozenAt) {
    const stamp = stampOf(frozenAt);
    if (stamp) return `Snapshot frozen ${stamp}`;
  }
  if (dataMode === "dynamic") return "Latest data (re-fetched when opened)";
  return null;
}
var CATALOG_RESOLUTIONS = [
  "viewer",
  "owner_snapshot",
  "defaults"
];
var CATALOG_RESOLUTION_LABELS = {
  viewer: "Viewer's catalog (their tags/labels)",
  owner_snapshot: "My catalog, frozen (owner snapshot)",
  defaults: "Global defaults only"
};
function isExternalTarget(target) {
  return target === "public";
}
var DEFAULT_CATALOG_RESOLUTION = "owner_snapshot";
function catalogResolutionDeliversOverlay(resolution) {
  return resolution === "owner_snapshot";
}

// src/serialization/serialize.ts
function toBase64Url(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const b64 = typeof btoa !== "undefined" ? btoa(binary) : Buffer.from(bytes).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromBase64Url(value) {
  const b64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = typeof atob !== "undefined" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function encodeViewState(state) {
  let payload = state;
  if (state.extra && ("snapshot" in state.extra || "catalogSnapshot" in state.extra)) {
    const { snapshot: _s, catalogSnapshot: _c, ...restExtra } = state.extra;
    payload = { ...state, extra: Object.keys(restExtra).length ? restExtra : void 0 };
  }
  return toBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}
function decodeViewState(token) {
  try {
    const json = new TextDecoder().decode(fromBase64Url(token));
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
function toSearchParams(search) {
  return typeof search === "string" ? new URLSearchParams(search) : search;
}
function readViewStateFromUrl(search, param = SV_URL_PARAM) {
  const token = toSearchParams(search).get(param);
  return token ? decodeViewState(token) : null;
}
function applyViewStateToUrl(url, state, param = SV_URL_PARAM) {
  const isAbsolute = /^[a-z]+:\/\//i.test(url);
  const base = isAbsolute ? void 0 : "http://_";
  const parsed = new URL(url, base);
  parsed.searchParams.set(param, encodeViewState(state));
  return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

// src/serialization/shareToken.ts
var SHARE_DEMO_ROUTE = "demo";
var ALWAYS_RESERVED = ["index.html"];
function segmentsOf(path) {
  return path.split("/").filter(Boolean);
}
function rawToken(location2, options) {
  const { basePath, queryParams = ["token"] } = options;
  const params = new URLSearchParams(location2.search);
  for (const name of queryParams) {
    const value = params.get(name);
    if (value) return value;
  }
  const segments = segmentsOf(location2.pathname);
  const last = segments[segments.length - 1];
  if (!last) return null;
  const reserved = /* @__PURE__ */ new Set([...ALWAYS_RESERVED, ...segmentsOf(basePath ?? "")]);
  if (reserved.has(last)) return null;
  try {
    return decodeURIComponent(last);
  } catch {
    return null;
  }
}
function readShareRouteFromUrl(location2, options = {}) {
  const token = rawToken(location2, options);
  if (!token) return { kind: "none" };
  if (token === SHARE_DEMO_ROUTE) return { kind: "demo" };
  return { kind: "share", token };
}
function createInMemorySavedViewsSource(seed = []) {
  const store = /* @__PURE__ */ new Map();
  for (const view of seed) store.set(view.id, view);
  const listViews = async ({ tableId, scope }) => [...store.values()].filter((v) => v.tableId === tableId && (scope ? v.scope === scope : true)).sort((a, b) => a.name.localeCompare(b.name));
  const links = /* @__PURE__ */ new Map();
  const opens = [];
  let linkSeq = 0;
  const randomToken = () => `tok_${(++linkSeq).toString(36)}${randomHex(8)}`;
  const clearDefaults = (tableId, scope, except) => {
    for (const view of store.values()) {
      if (view.tableId === tableId && view.scope === scope && view.id !== except && view.isDefault) {
        store.set(view.id, { ...view, isDefault: false });
      }
    }
  };
  return {
    async createShareLink(viewId, input) {
      const token = randomToken();
      const link = {
        id: `svl_${token}`,
        token,
        viewId,
        target: input.target,
        payloadVisibility: input.target === "public" ? "public" : "private",
        dataMode: input.dataMode ?? "static",
        catalogResolution: input.catalogResolution ?? "viewer",
        hasPassword: Boolean(input.password),
        expiresAt: input.expiresAt ?? null,
        maxViews: input.maxViews ?? null,
        viewCount: 0,
        revoked: false,
        targetUserIds: input.targetUserIds ?? [],
        targetTeamIds: input.targetTeamIds ?? [],
        shareUrl: `/shares/${token}`,
        createdAt: Date.now()
      };
      links.set(token, link);
      return link;
    },
    async listShareLinks(viewId) {
      return [...links.values()].filter((l) => l.viewId === viewId);
    },
    async updateShareLink(token, input) {
      const existing = links.get(token);
      if (!existing) throw new Error(`unknown share token: ${token}`);
      const next = {
        ...existing,
        expiresAt: input.expiresAt === void 0 ? existing.expiresAt : input.expiresAt,
        maxViews: input.maxViews === void 0 ? existing.maxViews : input.maxViews,
        revoked: input.revoked ?? existing.revoked,
        hasPassword: input.password === void 0 ? existing.hasPassword : Boolean(input.password)
      };
      links.set(token, next);
      return next;
    },
    async revokeShareLink(token) {
      const existing = links.get(token);
      if (existing) links.set(token, { ...existing, revoked: true });
    },
    async listShareOpens(viewId, options) {
      const tokens = new Set([...links.values()].filter((l) => l.viewId === viewId).map((l) => l.token));
      return opens.filter((o) => tokens.has(o.token)).sort((a, b) => b.openedAt - a.openedAt).slice(0, options?.limit ?? 50);
    },
    async resolveShare(token) {
      const link = links.get(token);
      if (!link || link.revoked) throw new Error("link not found or revoked");
      const now = Date.now();
      links.set(token, { ...link, viewCount: link.viewCount + 1, lastOpenedAt: now });
      opens.push({ token, accessor: "anonymous", accessorUserId: null, openedAt: now });
      const opened = store.get(link.viewId);
      if (opened) {
        store.set(opened.id, { ...opened, openCount: (opened.openCount ?? 0) + 1, lastOpenedAt: now });
      }
      const view = store.get(link.viewId);
      return {
        view: view ?? { id: link.viewId },
        link: {
          token: link.token,
          target: link.target,
          hasPassword: link.hasPassword,
          viewCount: link.viewCount + 1,
          payloadVisibility: link.payloadVisibility
        },
        dataMode: link.dataMode,
        catalogResolution: link.catalogResolution,
        payload: view?.state ? { state: view.state } : null,
        payloadUrl: null,
        payloadCdnPath: null,
        dynamicSeed: null,
        warnings: []
      };
    },
    list: listViews,
    async listWithMeta(query) {
      return { views: await listViews(query), truncated: false };
    },
    async get(id) {
      const view = store.get(id);
      if (!view) throw new Error(`unknown saved view: ${id}`);
      return view;
    },
    async create(view) {
      if (view.isDefault) clearDefaults(view.tableId, view.scope, view.id);
      store.set(view.id, view);
      return view;
    },
    async update(view) {
      const next = { ...view, updatedAt: Date.now() };
      if (next.isDefault) clearDefaults(next.tableId, next.scope, next.id);
      store.set(next.id, next);
      return next;
    },
    async remove(id) {
      store.delete(id);
    },
    async setDefault(tableId, scope, id) {
      clearDefaults(tableId, scope, id ?? void 0);
      if (id) {
        const view = store.get(id);
        if (view) store.set(id, { ...view, isDefault: true, updatedAt: Date.now() });
      }
    }
  };
}
var SavedViewsContext = React2.createContext(null);
function SavedViewsProvider({
  source,
  identity,
  permissions,
  children
}) {
  const { app, customerId, orgId, tenantId, userId } = identity;
  const { canSave, canShare, canDeleteShared = false } = permissions;
  const value = React2.useMemo(
    () => ({
      source,
      identity: { app, customerId, orgId, tenantId, userId },
      permissions: { canSave, canShare, canDeleteShared }
    }),
    [source, app, customerId, orgId, tenantId, userId, canSave, canShare, canDeleteShared]
  );
  return /* @__PURE__ */ jsx(SavedViewsContext.Provider, { value, children });
}
function useOptionalSavedViews() {
  return React2.useContext(SavedViewsContext);
}
var log = createLogger(`${SAVED_VIEWS_PACKAGE}:api-source`);
function toMs(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n < 1e12 ? Math.round(n * 1e3) : n;
}
function toMsOrNull(value) {
  if (value === null || value === void 0 || value === "") return null;
  const ms = toMs(value);
  return ms || null;
}
function toSeconds(value) {
  if (value === null || value === void 0) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n >= 1e12 ? Math.round(n / 1e3) : Math.round(n);
}
var MAX_LIST_PAGES = 20;
function viewFromWire(raw) {
  return {
    id: raw.id,
    name: raw.name ?? "",
    tableId: raw.table_id ?? raw.tableId ?? "",
    scope: isViewScope(raw.scope) ? raw.scope : "user",
    owner: raw.owner_user_id ?? raw.owner ?? null,
    isDefault: Boolean(raw.is_default ?? raw.isDefault ?? false),
    state: raw.view_state ?? raw.state ?? {},
    dataMode: raw.data_mode ?? raw.dataMode ?? void 0,
    payloadVisibility: raw.payload_visibility ?? raw.payloadVisibility ?? void 0,
    catalogResolution: raw.catalog_resolution ?? raw.catalogResolution ?? void 0,
    dynamicSeed: raw.dynamic_seed ?? raw.dynamicSeed ?? null,
    snapshotS3Key: raw.snapshot_s3_key ?? raw.snapshotS3Key ?? void 0,
    openCount: Number(raw.view_count ?? raw.openCount ?? 0) || 0,
    lastOpenedAt: toMsOrNull(raw.last_accessed_at ?? raw.lastOpenedAt),
    createdAt: toMs(raw.created_at ?? raw.createdAt),
    updatedAt: toMs(raw.updated_at ?? raw.updatedAt)
  };
}
function viewToWire(view, { forUpdate = false } = {}) {
  const body = {};
  if (view.tableId !== void 0) body.table_id = view.tableId;
  if (view.name !== void 0) body.name = view.name;
  if (view.scope !== void 0) body.scope = view.scope;
  if (view.isDefault !== void 0) body.is_default = view.isDefault;
  if (view.state !== void 0) body.view_state = view.state;
  if (view.dataMode !== void 0) body.data_mode = view.dataMode;
  if (view.payloadVisibility !== void 0 && !forUpdate) {
    body.payload_visibility = view.payloadVisibility;
  }
  if (view.catalogResolution !== void 0) body.catalog_resolution = view.catalogResolution;
  if (view.dynamicSeed !== void 0) body.dynamic_seed = view.dynamicSeed;
  if (view.snapshot !== void 0) body.snapshot = view.snapshot;
  if (view.catalogSnapshot !== void 0) body.catalog_snapshot = view.catalogSnapshot;
  return body;
}
function openFromWire(raw) {
  return {
    token: raw.token ?? "",
    accessor: raw.accessor_type === "authenticated" ? "authenticated" : "anonymous",
    accessorUserId: raw.accessor_user_id ?? null,
    accessorName: typeof raw.accessor_name === "string" && raw.accessor_name ? raw.accessor_name : null,
    openedAt: toMs(raw.created_at)
  };
}
function principalsFromWire(raw, idKey) {
  if (!Array.isArray(raw)) return void 0;
  return raw.filter((row) => Boolean(row) && typeof row === "object").map((row) => ({
    id: String(row[idKey] ?? ""),
    name: typeof row.name === "string" && row.name ? row.name : null
  })).filter((ref) => ref.id);
}
function linkFromWire(raw) {
  return {
    id: raw.id,
    token: raw.token,
    viewId: raw.view_id,
    target: raw.target,
    payloadVisibility: raw.payload_visibility,
    dataMode: raw.data_mode,
    catalogResolution: raw.catalog_resolution,
    hasPassword: Boolean(raw.has_password),
    expiresAt: toMsOrNull(raw.expires_at),
    maxViews: raw.max_views ?? null,
    viewCount: raw.view_count ?? 0,
    lastOpenedAt: toMsOrNull(raw.last_accessed_at),
    revoked: Boolean(raw.revoked),
    targetUserIds: raw.target_user_ids ?? [],
    targetTeamIds: raw.target_team_ids ?? [],
    targetUsers: principalsFromWire(raw.target_users, "user_id"),
    targetTeams: principalsFromWire(raw.target_teams, "team_id"),
    shareUrl: raw.share_url ?? void 0,
    // The backend says WHY a link has no copyable URL (no viewer base wired for
    // that audience) instead of omitting the field and letting the dialog
    // invent `${location.origin}/shares/{token}` — a host that serves no viewer.
    shareUrlUnavailable: raw.share_url_unavailable ?? void 0,
    createdAt: toMsOrNull(raw.created_at),
    createdBy: raw.created_by ?? null
  };
}
var PUBLIC_PAYLOAD_PREFIX = "/saved-views/public/";
function payloadNeedsCredentials(fromResolver, cdnPath, payloadUrl) {
  if (fromResolver || !payloadUrl) return false;
  return !(cdnPath ?? "").startsWith(PUBLIC_PAYLOAD_PREFIX);
}
function resolvedShareFromWire(raw, options) {
  const { token } = options;
  const cdnBaseUrl = options.cdnBaseUrl?.replace(/\/+$/, "");
  const resolved = raw.resolved ?? {};
  const link = raw.link;
  const cdnPath = resolved.payload_cdn_path ?? null;
  const fromResolver = resolved.payload_url ?? null;
  const payloadUrl = fromResolver ?? (cdnPath && cdnBaseUrl ? `${cdnBaseUrl}${cdnPath}` : null);
  return {
    payloadNeedsCredentials: payloadNeedsCredentials(fromResolver, cdnPath, payloadUrl),
    view: raw.view ?? {},
    link: link ? {
      token: link.token ?? token,
      target: link.target ?? "public",
      hasPassword: Boolean(link.has_password),
      viewCount: link.view_count ?? 0,
      maxViews: link.max_views ?? null,
      payloadVisibility: link.payload_visibility ?? "private"
    } : void 0,
    dataMode: resolved.data_mode ?? raw.data_mode ?? "static",
    catalogResolution: resolved.catalog_resolution ?? void 0,
    // The overlay the resolver actually built. It was computed server-side (an
    // `owner_snapshot` link returns the owner's frozen tags) and then dropped
    // here, so the three catalog options resolved identically for the viewer —
    // a picker whose choice never left this function.
    catalog: resolved.catalog ?? null,
    // The inline snapshot lives on `resolved`, with everything else the resolver
    // decided — it was read off the envelope ROOT, where nothing has ever been,
    // so the inline path could not fire and every static share paid a second
    // round-trip for a payload already in hand.
    payload: resolved.payload ?? raw.payload ?? null,
    payloadUrl,
    payloadCdnPath: cdnPath,
    dynamicSeed: resolved.dynamic_seed ?? null,
    warnings: (resolved.warnings ?? raw.warnings ?? []).map((w) => ({
      code: w.code,
      message: w.message,
      resourceType: w.resource_type,
      hiddenCount: w.hidden_count ?? 0,
      sampleIds: w.sample_ids ?? []
    }))
  };
}
function createApiSavedViewsSource(client, options = {}) {
  const base = options.basePath ?? "/saved-views";
  const sharesBase = options.sharesBasePath ?? "/shares";
  const cdnBaseUrl = options.cdnBaseUrl?.replace(/\/+$/, "");
  const viewPath = (id) => `${base}/${encodeURIComponent(id)}`;
  const viewSharesPath = (id) => `${viewPath(id)}/shares`;
  const sharePath = (token) => `${sharesBase}/${encodeURIComponent(token)}`;
  const resolvePath = (token) => options.authenticatedResolve ? `${sharePath(token)}/resolve` : sharePath(token);
  const currentUserId = options.currentUserId ?? null;
  async function listPage(query) {
    const all = [];
    let cursor;
    let truncated = false;
    let pagesRead = 0;
    for (let page = 0; page < MAX_LIST_PAGES; page++) {
      pagesRead = page + 1;
      const res = await client.get(base, {
        query: {
          table_id: query.tableId,
          ...query.scope ? { scope: query.scope } : {},
          ...cursor ? { cursor } : {}
        }
      });
      const data = unwrapData(res);
      if (Array.isArray(data)) {
        all.push(...data);
        cursor = void 0;
        break;
      }
      all.push(...data?.items ?? []);
      truncated = truncated || Boolean(data?.truncated);
      cursor = data?.next_cursor ?? void 0;
      if (!cursor) break;
    }
    if (cursor && pagesRead >= MAX_LIST_PAGES) truncated = true;
    return { views: all.map(viewFromWire), truncated };
  }
  async function listAll(query) {
    return (await listPage(query)).views;
  }
  const invalidateViews = () => client.invalidateMatching(base);
  return {
    list: listAll,
    listWithMeta: listPage,
    async get(id) {
      const res = await client.get(viewPath(id));
      const data = unwrapData(res);
      return viewFromWire(data?.view ?? data);
    },
    async create(view) {
      const created = viewFromWire(
        unwrapData(await client.post(base, viewToWire(view)))
      );
      invalidateViews();
      return created;
    },
    async update(view) {
      const updated = viewFromWire(
        unwrapData(await client.patch(viewPath(view.id), viewToWire(view, { forUpdate: true })))
      );
      invalidateViews();
      return updated;
    },
    async remove(id) {
      unwrapData(await client.delete(viewPath(id)));
      invalidateViews();
    },
    async setDefault(tableId, scope, id) {
      const current = await listAll({ tableId, scope });
      const stale = current.filter(
        (v) => v.isDefault && v.id !== id && (currentUserId == null || v.owner === currentUserId)
      );
      const cleared = await Promise.allSettled(
        stale.map(
          (v) => client.patch(viewPath(v.id), { is_default: false })
        )
      );
      cleared.forEach((outcome, index) => {
        if (outcome.status === "rejected") {
          log.warn("could not clear a stale default view", {
            viewId: stale[index]?.id,
            error: String(outcome.reason)
          });
        }
      });
      if (id) {
        unwrapData(await client.patch(viewPath(id), { is_default: true }));
      }
      invalidateViews();
    },
    async createShareLink(viewId, input) {
      const body = {
        target: input.target,
        password: input.password,
        expires_at: toSeconds(input.expiresAt),
        max_views: input.maxViews,
        data_mode: input.dataMode,
        catalog_resolution: input.catalogResolution,
        target_user_ids: input.targetUserIds,
        target_team_ids: input.targetTeamIds
      };
      return linkFromWire(unwrapData(await client.post(viewSharesPath(viewId), body)));
    },
    async listShareLinks(viewId) {
      const res = await client.get(
        viewSharesPath(viewId),
        { cacheTtlMs: 0 }
      );
      const data = unwrapData(res);
      const items = Array.isArray(data) ? data : data?.items ?? [];
      return items.map(linkFromWire);
    },
    async updateShareLink(token, input) {
      const body = {
        password: input.password,
        ...input.expiresAt !== void 0 ? { expires_at: toSeconds(input.expiresAt) } : {},
        max_views: input.maxViews,
        revoked: input.revoked
      };
      return linkFromWire(unwrapData(await client.patch(sharePath(token), body)));
    },
    async revokeShareLink(token) {
      unwrapData(await client.delete(sharePath(token)));
    },
    async listShareOpens(viewId, options2) {
      const res = await client.get(
        `${viewPath(viewId)}/access`,
        { cacheTtlMs: 0, ...options2?.limit ? { query: { limit: String(options2.limit) } } : {} }
      );
      const data = unwrapData(res);
      const items = Array.isArray(data) ? data : data?.items ?? [];
      return items.map(openFromWire);
    },
    async resolveShare(token, password, reload) {
      const res = await client.get(resolvePath(token), {
        cacheTtlMs: 0,
        query: reload ? { reload: "true" } : void 0,
        headers: password ? { "X-Share-Password": password } : void 0
      });
      return resolvedShareFromWire(unwrapData(res), {
        token,
        ...cdnBaseUrl ? { cdnBaseUrl } : {}
      });
    }
  };
}
var log2 = createLogger(SAVED_VIEWS_PACKAGE);
function useSavedViews(options) {
  const { source, tableId, identity, onApply, autoLoad = true } = options;
  const [views, setViews] = React2.useState([]);
  const [status, setStatus] = React2.useState("idle");
  const [error, setError] = React2.useState(void 0);
  const [activeViewId, setActiveViewId] = React2.useState(null);
  const [truncated, setTruncated] = React2.useState(false);
  const sourceRef = React2.useRef(source);
  React2.useEffect(() => {
    sourceRef.current = source;
  }, [source]);
  const onApplyRef = React2.useRef(onApply);
  React2.useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);
  const identityKey = [
    identity.app,
    identity.customerId,
    identity.orgId,
    identity.tenantId,
    identity.userId
  ].join("|");
  const store = React2.useMemo(
    () => createScopedStorage(identity),
    // identity fields are captured by identityKey; the object identity may churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identityKey]
  );
  const activeName = `${tableId}:${SV_ACTIVE_SUFFIX}`;
  const owner = identity.userId ?? null;
  const runRef = React2.useRef(0);
  const load = React2.useCallback(
    async (applyInitial) => {
      const runId = ++runRef.current;
      setStatus("loading");
      setError(void 0);
      try {
        const source_ = sourceRef.current;
        const page = source_.listWithMeta ? await source_.listWithMeta({ tableId }) : { views: await source_.list({ tableId }), truncated: false };
        if (runId !== runRef.current) return;
        const next = page.views;
        setViews(next);
        setTruncated(page.truncated);
        setStatus("ready");
        const persisted = store.getAt(activeName, "user", null);
        const resolved = persisted && next.find((v) => v.id === persisted) || next.find((v) => v.isDefault && v.owner === owner) || next.find((v) => v.isDefault) || null;
        setActiveViewId(resolved?.id ?? null);
        if (applyInitial && resolved) onApplyRef.current?.(resolved.state, resolved);
      } catch (err) {
        if (runId !== runRef.current) return;
        log2.error("failed to load saved views", { tableId, error: String(err) });
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      }
    },
    [tableId, store, activeName, owner]
  );
  React2.useEffect(() => {
    runRef.current += 1;
    setViews([]);
    setActiveViewId(null);
    if (autoLoad) void load(true);
  }, [autoLoad, load]);
  const refresh = React2.useCallback(() => void load(false), [load]);
  const apply = React2.useCallback(
    (id) => {
      const view = id ? views.find((v) => v.id === id) ?? null : null;
      setActiveViewId(view?.id ?? null);
      store.set(activeName, view?.id ?? null, "user");
      onApplyRef.current?.(view?.state ?? {}, view);
    },
    [views, store, activeName]
  );
  const save = React2.useCallback(
    async (name, state, scope, serving) => {
      const now = Date.now();
      const view = {
        id: newViewId(),
        name: name.trim(),
        tableId,
        scope,
        owner,
        state,
        ...serving ?? {},
        createdAt: now,
        updatedAt: now
      };
      try {
        const created = await sourceRef.current.create(view);
        notifySuccess(`Saved view "${created.name}"`);
        setActiveViewId(created.id);
        store.set(activeName, created.id, "user");
        await load(false);
        return created;
      } catch (err) {
        notifyError(err, { title: "Couldn't save the view" });
        return null;
      }
    },
    [tableId, owner, store, activeName, load]
  );
  const rename = React2.useCallback(
    async (id, name) => {
      const existing = views.find((v) => v.id === id);
      if (!existing) return;
      const prevViews = views;
      const trimmed = name.trim();
      setViews((prev) => prev.map((v) => v.id === id ? { ...v, name: trimmed } : v));
      try {
        await sourceRef.current.update({ ...existing, name: trimmed });
        await load(false);
      } catch (err) {
        setViews(prevViews);
        notifyError(err, { title: "Couldn't rename the view" });
      }
    },
    [views, load]
  );
  const remove = React2.useCallback(
    async (id) => {
      const prevViews = views;
      const prevActiveId = activeViewId;
      setViews((prev) => prev.filter((v) => v.id !== id));
      if (activeViewId === id) {
        setActiveViewId(null);
        store.set(activeName, null, "user");
      }
      try {
        await sourceRef.current.remove(id);
        notifySuccess("View deleted");
        await load(false);
      } catch (err) {
        setViews(prevViews);
        if (prevActiveId === id) {
          setActiveViewId(prevActiveId);
          store.set(activeName, prevActiveId, "user");
        }
        notifyError(err, { title: "Couldn't delete the view" });
      }
    },
    [views, activeViewId, store, activeName, load]
  );
  const setDefault = React2.useCallback(
    async (id, scope) => {
      const prevViews = views;
      setViews(
        (prev) => prev.map((v) => {
          if (v.scope !== scope) return v;
          if (v.id === id) return { ...v, isDefault: true };
          if (v.isDefault) return { ...v, isDefault: false };
          return v;
        })
      );
      try {
        await sourceRef.current.setDefault(tableId, scope, id);
        await load(false);
      } catch (err) {
        setViews(prevViews);
        notifyError(err, { title: "Couldn't update the default view" });
      }
    },
    [views, tableId, load]
  );
  const shareLink = React2.useCallback(
    (state, baseUrl) => {
      const url = baseUrl ?? (typeof location !== "undefined" ? location.href : "/");
      const result = applyViewStateToUrl(url, state);
      if (result.length > SV_MAX_SHARE_URL_LENGTH) {
        log2.warn("generated share link exceeds safe URL length limit", {
          tableId,
          length: result.length,
          limit: SV_MAX_SHARE_URL_LENGTH
        });
      }
      return result;
    },
    [tableId]
  );
  const activeView = React2.useMemo(
    () => views.find((v) => v.id === activeViewId) ?? null,
    [views, activeViewId]
  );
  return {
    views,
    status,
    error,
    truncated,
    activeViewId,
    activeView,
    refresh,
    apply,
    save,
    rename,
    remove,
    setDefault,
    shareLink
  };
}
var log3 = createLogger(`${SAVED_VIEWS_PACKAGE}:shares`);
function useShareLinks(options) {
  const { source, viewId, autoLoad = true } = options;
  const supported = typeof source.listShareLinks === "function";
  const [links, setLinks] = React2.useState([]);
  const [loading, setLoading] = React2.useState(false);
  const [error, setError] = React2.useState(void 0);
  const sourceRef = React2.useRef(source);
  React2.useEffect(() => {
    sourceRef.current = source;
  }, [source]);
  const load = React2.useCallback(async () => {
    if (!viewId || !sourceRef.current.listShareLinks) {
      setLinks([]);
      return;
    }
    setLoading(true);
    setError(void 0);
    try {
      setLinks(await sourceRef.current.listShareLinks(viewId));
    } catch (err) {
      log3.error("failed to load share links", { viewId, error: String(err) });
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [viewId]);
  React2.useEffect(() => {
    if (autoLoad) void load();
  }, [autoLoad, load]);
  const create = React2.useCallback(
    async (input) => {
      if (!viewId || !sourceRef.current.createShareLink) return null;
      try {
        const link = await sourceRef.current.createShareLink(viewId, input);
        notifySuccess("Share link created");
        setLinks((prev) => [link, ...prev]);
        return link;
      } catch (err) {
        notifyError(err, { title: "Couldn't create the share link" });
        return null;
      }
    },
    [viewId]
  );
  const update = React2.useCallback(
    async (token, input) => {
      if (!sourceRef.current.updateShareLink) return null;
      try {
        const link = await sourceRef.current.updateShareLink(token, input);
        setLinks((prev) => prev.map((l) => l.token === token ? link : l));
        return link;
      } catch (err) {
        notifyError(err, { title: "Couldn't update the share link" });
        return null;
      }
    },
    []
  );
  const revoke = React2.useCallback(
    async (token) => {
      if (!sourceRef.current.revokeShareLink) return;
      try {
        await sourceRef.current.revokeShareLink(token);
        notifySuccess("Share link revoked");
        setLinks((prev) => prev.map((l) => l.token === token ? { ...l, revoked: true } : l));
      } catch (err) {
        notifyError(err, { title: "Couldn't revoke the share link" });
      }
    },
    []
  );
  return {
    links,
    loading,
    error,
    refresh: () => void load(),
    create,
    update,
    revoke,
    supported
  };
}
var log4 = createLogger(`${SAVED_VIEWS_PACKAGE}:resolve-share`);
var SHARE_ERROR_CODES = {
  passwordRequired: "SHARE_LINK_PASSWORD_REQUIRED",
  authRequired: "SHARE_LINK_AUTH_REQUIRED",
  forbidden: "SHARE_LINK_FORBIDDEN",
  revoked: "SHARE_LINK_REVOKED",
  expired: "SHARE_LINK_EXPIRED",
  notFound: "SHARE_LINK_NOT_FOUND"
};
function shareErrorCodeOf(err) {
  if (!(err instanceof ApiError)) return void 0;
  const domain = err.envelope?.error?.error_code;
  if (domain && Object.values(SHARE_ERROR_CODES).includes(domain)) return domain;
  return err.code;
}
var EMPTY_WARNINGS = [];
function describeSnapshotGap(outcome) {
  if (outcome.dataMode !== "static") return null;
  if (outcome.hasPayload) return null;
  if (!outcome.hasPayloadUrl) {
    return "This shared view has no stored snapshot. It may have been shared before its data was frozen \u2014 ask the owner to re-share it.";
  }
  if (outcome.threw) {
    return "Couldn't reach the storage holding this view's data. Check your connection and reload.";
  }
  const status = outcome.status;
  if (status === void 0 || status >= 200 && status < 300) return null;
  if (status === 403) {
    return "Access to this view's data was refused (403). The link's download grant may have expired \u2014 reload the page, or ask the owner for a fresh link.";
  }
  if (status === 404) {
    return "This view's data is no longer stored (404). The owner may have deleted or re-saved the view.";
  }
  return `Couldn't load this view's data (HTTP ${status}).`;
}
function statusForCode(code) {
  switch (code) {
    case SHARE_ERROR_CODES.passwordRequired:
      return "password-required";
    case SHARE_ERROR_CODES.authRequired:
      return "auth-required";
    case SHARE_ERROR_CODES.forbidden:
      return "forbidden";
    case SHARE_ERROR_CODES.notFound:
      return "not-found";
    case SHARE_ERROR_CODES.revoked:
      return "revoked";
    case SHARE_ERROR_CODES.expired:
      return "expired";
    default:
      return "error";
  }
}
function useResolveShare(options) {
  const { source, token, fetchSnapshot = true, autoLoad = true } = options;
  const [status, setStatus] = React2.useState("idle");
  const [refreshing, setRefreshing] = React2.useState(false);
  const readyRef = React2.useRef(false);
  const [resolved, setResolved] = React2.useState(void 0);
  const [snapshot, setSnapshot] = React2.useState(void 0);
  const [snapshotError, setSnapshotError] = React2.useState(void 0);
  const [error, setError] = React2.useState(void 0);
  const [errorCode, setErrorCode] = React2.useState(void 0);
  const [errorCause, setErrorCause] = React2.useState(void 0);
  const [session, setSession] = React2.useState({ source, token });
  const fetchController = React2.useRef(null);
  const passwordRef = React2.useRef(void 0);
  const runIdRef = React2.useRef(0);
  const run = React2.useCallback(
    async (reload2) => {
      const runId = ++runIdRef.current;
      fetchController.current?.abort();
      const controller = new AbortController();
      fetchController.current = controller;
      const resolveShare = source.resolveShare;
      if (!token) {
        setStatus("idle");
        return;
      }
      if (!resolveShare) {
        setStatus("error");
        setError("This source cannot open shared views.");
        return;
      }
      setRefreshing(readyRef.current);
      if (!readyRef.current) {
        setStatus("loading");
        setResolved(void 0);
        setSnapshot(void 0);
        setSnapshotError(void 0);
      }
      setError(void 0);
      setErrorCode(void 0);
      setErrorCause(void 0);
      try {
        const result = await resolveShare(token, passwordRef.current, reload2);
        if (runId !== runIdRef.current) return;
        let snap = result.payload ?? null;
        let status2;
        let threw = false;
        let invalidPayload = false;
        if (fetchSnapshot && !snap && result.dataMode === "static" && result.payloadUrl) {
          try {
            const res = await fetch(result.payloadUrl, {
              credentials: result.payloadNeedsCredentials ? "include" : "omit",
              signal: controller.signal
            });
            if (runId !== runIdRef.current) return;
            status2 = res.status;
            if (res.ok) snap = await res.json();
            else
              log4.warn("snapshot fetch non-2xx", {
                status: res.status,
                withCredentials: Boolean(result.payloadNeedsCredentials)
              });
          } catch (err) {
            if (runId !== runIdRef.current) return;
            threw = true;
            log4.warn("snapshot fetch failed", { error: String(err) });
          }
        }
        if (runId !== runIdRef.current) return;
        if (snap !== null && (typeof snap !== "object" || Array.isArray(snap))) {
          snap = null;
          invalidPayload = true;
        }
        setResolved(result);
        setSnapshot(snap);
        setSnapshotError(
          invalidPayload ? "This view\u2019s data is not in a supported format. Ask the owner to share it again." : describeSnapshotGap({
            dataMode: result.dataMode,
            hasPayload: Boolean(snap),
            hasPayloadUrl: Boolean(result.payloadUrl),
            status: status2,
            threw
          }) ?? void 0
        );
        readyRef.current = true;
        setRefreshing(false);
        setStatus("ready");
      } catch (err) {
        if (runId !== runIdRef.current) return;
        readyRef.current = false;
        setRefreshing(false);
        setResolved(void 0);
        setSnapshot(void 0);
        setSnapshotError(void 0);
        const code = shareErrorCodeOf(err);
        const next = statusForCode(code);
        setErrorCode(code);
        setErrorCause(err);
        setStatus(next);
        setError(next === "password-required" && passwordRef.current === void 0 ? void 0 : err instanceof Error ? err.message : String(err));
        if (next === "error" || next === "not-found" || next === "expired" || next === "revoked") {
          log4.error("share resolve failed", { code });
        }
      }
    },
    [source, token, fetchSnapshot]
  );
  React2.useEffect(() => {
    setSession({ source, token });
    readyRef.current = false;
    setRefreshing(false);
    passwordRef.current = void 0;
    setError(void 0);
    setErrorCode(void 0);
    setErrorCause(void 0);
    setResolved(void 0);
    setSnapshot(void 0);
    setSnapshotError(void 0);
    if (autoLoad && token) void run(false);
    else setStatus("idle");
    return () => {
      ++runIdRef.current;
      fetchController.current?.abort();
    };
  }, [autoLoad, source, token, run]);
  const submitPassword = React2.useCallback(
    (password) => {
      passwordRef.current = password;
      void run(false);
    },
    [run]
  );
  const reload = React2.useCallback(
    (opts) => {
      void run(Boolean(opts?.reload));
    },
    [run]
  );
  if (session.source !== source || session.token !== token) {
    return {
      status: token && autoLoad ? "loading" : "idle",
      refreshing: false,
      warnings: EMPTY_WARNINGS,
      needsPassword: false,
      needsAuth: false,
      submitPassword,
      reload
    };
  }
  return {
    status,
    refreshing,
    resolved,
    snapshot,
    snapshotError,
    warnings: resolved?.warnings ?? EMPTY_WARNINGS,
    error,
    errorCode,
    errorCause,
    needsPassword: status === "password-required" || status === "loading" && passwordRef.current !== void 0,
    needsAuth: status === "auth-required",
    submitPassword,
    reload
  };
}

// src/data/useGraphViews.ts
var GRAPHS_TABLE_ID = "graphs";
function useGraphViews(options) {
  const { source, identity, tableId = GRAPHS_TABLE_ID, onApply, autoLoad } = options;
  return useSavedViews({ source, identity, tableId, onApply, autoLoad });
}
var DEFAULT_NAME_PLACEHOLDER = "e.g. High-value pending";
function SaveCurrentViewForm({
  canShare,
  defaultScope,
  allowedScopes,
  policyNote,
  namePlaceholder,
  onSave,
  onCancel
}) {
  const permitted = canShare ? VIEW_SCOPES : ["user"];
  const scopes = permitted.filter((option) => !allowedScopes || allowedScopes.includes(option));
  const [name, setName] = React2.useState("");
  const [scope, setScope] = React2.useState(
    scopes.includes(defaultScope) ? defaultScope : scopes[0] ?? defaultScope
  );
  const [busy, setBusy] = React2.useState(false);
  const blocked = scopes.length === 0;
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onSave(trimmed, scope);
      onCancel();
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "space-y-4 py-2", children: [
      /* @__PURE__ */ jsx(Field, { label: "Name", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: name,
          maxLength: SV_MAX_NAME_LENGTH,
          autoFocus: true,
          placeholder: namePlaceholder,
          onChange: (event) => setName(event.target.value),
          onKeyDown: (event) => {
            if (event.key === "Enter") void submit();
          }
        }
      ) }),
      scopes.length > 1 && /* @__PURE__ */ jsx(
        Field,
        {
          label: "Visibility",
          hint: scope === "tenant" ? "Everyone in this tenant who can read this resource will see and apply this view \u2014 including people outside your team." : "Only you can see this view.",
          ownsControl: true,
          children: /* @__PURE__ */ jsxs(Select, { value: scope, onValueChange: (value) => setScope(value), children: [
            /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
            /* @__PURE__ */ jsx(SelectContent, { children: scopes.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option, children: SCOPE_LABELS[option] }, option)) })
          ] })
        }
      ),
      policyNote ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: policyNote }) : null
    ] }),
    /* @__PURE__ */ jsxs(DialogFooter, { children: [
      /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: onCancel, children: "Cancel" }),
      /* @__PURE__ */ jsx(Button, { onClick: () => void submit(), disabled: !name.trim() || busy || blocked, children: "Save" })
    ] })
  ] });
}
function SaveCurrentViewDialog({
  open,
  onOpenChange,
  canShare,
  defaultScope = "user",
  allowedScopes,
  policyNote,
  description = "Save the current columns, filters, and sort so you can return to them in one click.",
  namePlaceholder = DEFAULT_NAME_PLACEHOLDER,
  onSave
}) {
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: "Save view" }),
      /* @__PURE__ */ jsx(DialogDescription, { children: description })
    ] }),
    open && /* @__PURE__ */ jsx(
      SaveCurrentViewForm,
      {
        canShare,
        defaultScope,
        ...allowedScopes ? { allowedScopes } : {},
        ...policyNote ? { policyNote } : {},
        namePlaceholder,
        onSave,
        onCancel: () => onOpenChange(false)
      },
      String(open)
    )
  ] }) });
}
function scopeGroups(views) {
  const present = new Set(views.map((view) => view.scope));
  const ordered = [...VIEW_SCOPES];
  for (const scope of present) {
    if (!ordered.includes(scope)) ordered.push(scope);
  }
  return ordered.filter((scope) => present.has(scope));
}
function SavedViewsMenu({
  controller,
  onSaveCurrent,
  canSave = true,
  onManage,
  className
}) {
  const { views, status, error, activeView, activeViewId, apply, refresh, truncated } = controller;
  const [search, setSearch] = React2.useState("");
  const label = activeView ? activeView.name : "Views";
  const query = search.trim().toLowerCase();
  const filteredViews = query ? views.filter((view) => view.name.toLowerCase().includes(query)) : views;
  const showSearch = views.length >= 5 || truncated;
  const outcome = listOutcome({
    count: views.length,
    loading: status === "loading" || status === "idle",
    failed: status === "error"
  });
  return /* @__PURE__ */ jsxs(DropdownMenu, { onOpenChange: (open) => {
    if (!open) setSearch("");
  }, children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", className: cn("gap-2", className), children: label }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "start", className: "w-60", children: [
      showSearch && /* @__PURE__ */ jsx("div", { className: "p-1.5", onClick: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx(SearchIcon, { className: "pointer-events-none absolute left-2 top-2.5 size-3.5 text-muted-foreground" }),
        /* @__PURE__ */ jsx(
          Input,
          {
            value: search,
            onChange: (e) => setSearch(e.target.value),
            onKeyDown: (e) => e.stopPropagation(),
            placeholder: "Filter views\u2026",
            className: "h-8 pl-7 text-xs"
          }
        )
      ] }) }),
      outcome === "loading" && /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-muted-foreground", children: "Loading your views\u2026" }),
      outcome === "failed" && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-muted-foreground", children: error ?? "Couldn't load your views" }),
        /* @__PURE__ */ jsx(
          DropdownMenuItem,
          {
            onSelect: (event) => {
              event.preventDefault();
              refresh();
            },
            children: "Couldn't load your views \xB7 Retry"
          }
        )
      ] }),
      outcome === "empty" && /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-muted-foreground", children: truncated ? "This surface has too many saved views to list. Filter above to narrow it." : "No saved views yet" }),
      truncated && views.length > 0 && !query && /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-xs text-muted-foreground", children: "Showing the most recent \u2014 some views aren't listed" }),
      status === "error" && views.length > 0 && // Rows, and a refresh that failed. Neither hides the other: the views
      // below are real, and they are no longer current.
      /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-xs text-muted-foreground", children: "Couldn't refresh \u2014 these are the views that last loaded" }),
      query && filteredViews.length === 0 && views.length > 0 && /* @__PURE__ */ jsxs(DropdownMenuLabel, { className: "font-normal text-xs text-muted-foreground", children: [
        'No views match "',
        search,
        '"'
      ] }),
      scopeGroups(filteredViews).map((scope) => {
        const scoped = filteredViews.filter((view) => view.scope === scope);
        if (scoped.length === 0) return null;
        return /* @__PURE__ */ jsxs(React2.Fragment, { children: [
          /* @__PURE__ */ jsx(DropdownMenuLabel, { children: SCOPE_LABELS[scope] }),
          scoped.map((view) => /* @__PURE__ */ jsxs(
            DropdownMenuItem,
            {
              onSelect: () => {
                apply(view.id);
                setSearch("");
              },
              className: "gap-2",
              children: [
                /* @__PURE__ */ jsx("span", { className: "flex size-3.5 items-center justify-center text-primary", children: view.id === activeViewId ? /* @__PURE__ */ jsx(CheckIcon, {}) : null }),
                /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: view.name }),
                view.isDefault && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Default" })
              ]
            },
            view.id
          ))
        ] }, scope);
      }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      canSave && /* @__PURE__ */ jsx(DropdownMenuItem, { onSelect: onSaveCurrent, children: "Save current view\u2026" }),
      onManage && views.length > 0 && /* @__PURE__ */ jsx(DropdownMenuItem, { onSelect: onManage, children: "Manage views\u2026" }),
      /* @__PURE__ */ jsx(
        DropdownMenuItem,
        {
          onSelect: () => {
            apply(null);
            setSearch("");
          },
          disabled: !activeViewId,
          children: "Reset"
        }
      )
    ] })
  ] });
}
function copyShareLink(link) {
  void writeClipboardText(link).then(
    (ok) => ok ? notifyInfo("Share link copied") : notifyInfo("Copy failed", "Copy the link from the address bar instead.")
  );
}
function SavedViewsList({
  controller,
  canManageShared = true,
  canManagePersonal = true,
  canDeleteShared,
  showCopyLink = false,
  emptyDescription,
  renderBadge,
  className
}) {
  const {
    views,
    status,
    error,
    truncated,
    activeViewId,
    apply,
    rename,
    remove,
    setDefault,
    shareLink
  } = controller;
  const [search, setSearch] = React2.useState("");
  const [renamingId, setRenamingId] = React2.useState(null);
  const [draftName, setDraftName] = React2.useState("");
  const startRename = (view) => {
    setRenamingId(view.id);
    setDraftName(view.name);
  };
  const commitRename = (view) => {
    const next = draftName.trim();
    setRenamingId(null);
    if (next && next !== view.name) void rename(view.id, next);
  };
  const outcome = listOutcome({
    count: views.length,
    loading: status === "loading" || status === "idle",
    failed: status === "error"
  });
  if (outcome === "loading") {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: [0, 1, 2].map((index) => /* @__PURE__ */ jsx(Skeleton, { className: "h-12 w-full" }, index)) });
  }
  if (outcome === "failed") {
    return /* @__PURE__ */ jsx(ErrorState, { error: error ?? "Failed to load saved views", onRetry: controller.refresh, className });
  }
  if (outcome === "empty") {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: truncated ? "Too many saved views to list" : "No saved views",
        description: truncated ? "This surface has more saved views than can be listed at once. Filter to narrow it." : emptyDescription ?? "Save the current columns, filters, and sort to reuse them later.",
        className
      }
    );
  }
  const query = search.trim().toLowerCase();
  const filteredViews = query ? views.filter((view) => view.name.toLowerCase().includes(query)) : views;
  const showSearch = views.length >= 4 || truncated;
  const canManage = (view) => view.scope === "user" ? canManagePersonal : canManageShared;
  const canDelete = (view) => view.scope === "user" ? canManagePersonal : canDeleteShared ?? canManageShared;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    status === "error" && // Rows, and a reload that failed. Neither hides the other: the views
    // below are real, and a delete that just happened may not be reflected.
    /* @__PURE__ */ jsxs("p", { role: "alert", className: "flex flex-wrap items-center gap-2 rounded-md border border-dashed border-destructive/50 p-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("span", { children: "Couldn't refresh \u2014 these are the views that last loaded." }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          size: "sm",
          className: "h-6 px-2",
          onClick: controller.refresh,
          children: "Retry"
        }
      )
    ] }),
    showSearch && /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(SearchIcon, { className: "pointer-events-none absolute left-2.5 top-2.5 size-3.5 text-muted-foreground" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          value: search,
          onChange: (event) => setSearch(event.target.value),
          placeholder: "Search saved views\u2026",
          className: "h-8 pl-8 text-xs"
        }
      )
    ] }),
    filteredViews.length === 0 && query ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No matching views",
        description: `No saved views match "${search}". Clear the search to see all views.`
      }
    ) : /* @__PURE__ */ jsxs("ul", { className: "divide-y rounded-md border", children: [
      truncated && !query && /* @__PURE__ */ jsx("li", { className: "px-3 py-2 text-xs text-muted-foreground", children: "Showing the most recent \u2014 some views aren't listed." }),
      filteredViews.map((view) => {
        const manageable = canManage(view);
        const deletable = canDelete(view);
        const isActive = view.id === activeViewId;
        return /* @__PURE__ */ jsxs("li", { className: cn("flex items-start gap-3 px-3 py-2", isActive && "bg-muted/50"), children: [
          renamingId === view.id ? /* @__PURE__ */ jsx("div", { className: "flex min-w-0 flex-1 items-center", children: /* @__PURE__ */ jsx(
            Input,
            {
              autoFocus: true,
              "aria-label": "View name",
              "data-saved-view-rename": "",
              value: draftName,
              maxLength: SV_MAX_NAME_LENGTH,
              onChange: (event) => setDraftName(event.target.value),
              onKeyDown: (event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commitRename(view);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setRenamingId(null);
                }
              },
              onBlur: () => commitRename(view),
              className: "h-8 text-sm"
            }
          ) }) : /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => apply(view.id),
              className: "flex min-w-0 flex-1 flex-col items-start gap-1 text-left",
              children: [
                /* @__PURE__ */ jsx("span", { className: "w-full truncate text-sm font-medium", title: view.name, children: view.name }),
                /* @__PURE__ */ jsxs("span", { className: "flex min-w-0 flex-wrap items-center gap-1", children: [
                  /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0 text-xs", children: SCOPE_LABELS[view.scope] }),
                  renderBadge?.(view),
                  view.isDefault && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "shrink-0 text-xs", children: "Default" })
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": "Rename view",
                title: "Rename view",
                disabled: !manageable || renamingId === view.id,
                className: "size-8",
                onClick: () => startRename(view),
                children: /* @__PURE__ */ jsx(PencilIcon, {})
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": view.isDefault ? "Unset default" : "Set as default",
                title: view.isDefault ? "Unset default" : "Set as default",
                disabled: !manageable,
                className: cn("size-8", view.isDefault && "text-highlight"),
                onClick: () => void setDefault(view.isDefault ? null : view.id, view.scope),
                children: /* @__PURE__ */ jsx(StarIcon, { fill: view.isDefault ? "currentColor" : "none" })
              }
            ),
            showCopyLink && /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": "Copy share link",
                title: "Copy share link",
                className: "size-8",
                onClick: () => copyShareLink(shareLink(view.state)),
                children: /* @__PURE__ */ jsx(ShareIcon, {})
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": "Delete view",
                title: "Delete view",
                disabled: !deletable,
                className: "size-8 text-muted-foreground hover:text-destructive",
                onClick: () => void remove(view.id),
                children: /* @__PURE__ */ jsx(TrashIcon, {})
              }
            )
          ] })
        ] }, view.id);
      })
    ] })
  ] });
}
function ManageSavedViewsDialog({
  open,
  onOpenChange,
  controller,
  canManagePersonal,
  canManageShared,
  canDeleteShared
}) {
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(
    DialogContent,
    {
      className: "max-w-lg",
      onEscapeKeyDown: (event) => {
        if (event.target?.hasAttribute?.("data-saved-view-rename")) {
          event.preventDefault();
        }
      },
      children: [
        /* @__PURE__ */ jsxs(DialogHeader, { children: [
          /* @__PURE__ */ jsx(DialogTitle, { children: "Saved views" }),
          /* @__PURE__ */ jsx(DialogDescription, { children: "Rename, delete or set a default. Select a view to apply it." })
        ] }),
        /* @__PURE__ */ jsx(
          SavedViewsList,
          {
            controller,
            canManagePersonal,
            canManageShared,
            canDeleteShared,
            className: "max-h-[60vh] overflow-y-auto"
          }
        )
      ]
    }
  ) });
}
function describeOpener(open, userLabel) {
  if (open.accessor === "anonymous" || !open.accessorUserId) return "Someone with the link";
  return open.accessorName || userLabel?.(open.accessorUserId) || "A signed-in member";
}
function describeVia(open, links) {
  const link = links.find((l) => l.token === open.token);
  if (!link) return "a link that no longer exists";
  const audience = SHARE_TARGET_LABELS[link.target] ?? link.target;
  return link.revoked ? `${audience} (since revoked)` : audience;
}
function ShareOpensPanel({
  source,
  viewId,
  links,
  userLabel,
  limit = 10
}) {
  const list = source.listShareOpens;
  const [state, setState] = React2.useState({ kind: "loading" });
  const [attempt, setAttempt] = React2.useState(0);
  const [refreshing, setRefreshing] = React2.useState(false);
  const now = useNow(3e4);
  const revision = useResourceRevision(SAVED_VIEW_ACCESS_RESOURCE);
  React2.useEffect(() => {
    if (!list || typeof document === "undefined") return void 0;
    const tick = () => {
      if (document.visibilityState === "visible") setAttempt((n) => n + 1);
    };
    const timer = window.setInterval(tick, SV_OPENS_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [list]);
  React2.useEffect(() => {
    if (!list) return void 0;
    let live = true;
    setState((prev) => prev.kind === "ready" ? prev : { kind: "loading" });
    setRefreshing(true);
    list(viewId, { limit }).then(
      (opens) => {
        if (live) setState({ kind: "ready", opens, at: Date.now() });
      },
      () => {
        if (live) setState((prev) => prev.kind === "ready" ? prev : { kind: "error" });
      }
    ).finally(() => {
      if (live) setRefreshing(false);
    });
    return () => {
      live = false;
    };
  }, [list, viewId, limit, attempt, revision]);
  if (!list) return null;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-sm font-medium", children: "Recent opens" }),
      state.kind === "ready" ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
        /* @__PURE__ */ jsx("span", { "aria-live": "polite", children: refreshing ? "Checking\u2026" : `Updated ${formatRelativeTime(state.at, { unit: "milliseconds", nowMs: Math.max(now, state.at) })}` }),
        /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: refreshing, onClick: () => setAttempt((n) => n + 1), children: "Refresh" })
      ] }) : null
    ] }),
    state.kind === "loading" ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Loading who opened these links\u2026" }) : state.kind === "error" ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-destructive", children: [
      /* @__PURE__ */ jsx("span", { children: "Couldn't load who opened these links." }),
      /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", onClick: () => setAttempt((n) => n + 1), children: "Retry" })
    ] }) : state.opens.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Nobody has opened a link to this view yet." }) : /* @__PURE__ */ jsx("ul", { className: "divide-y divide-border/60 rounded-md border", children: state.opens.map((open, i) => /* @__PURE__ */ jsxs(
      "li",
      {
        className: "flex items-center justify-between gap-3 px-3 py-1.5 text-xs",
        children: [
          /* @__PURE__ */ jsxs("span", { className: "min-w-0 truncate", children: [
            /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: describeOpener(open, userLabel) }),
            " ",
            /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
              "via ",
              describeVia(open, links)
            ] })
          ] }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "shrink-0 text-muted-foreground",
              title: new Date(open.openedAt).toLocaleString(),
              children: formatRelativeTime(open.openedAt, { unit: "milliseconds", nowMs: now })
            }
          )
        ]
      },
      `${open.token}:${open.openedAt}:${i}`
    )) })
  ] });
}
var EMPTY = { users: [], teams: [], currentUser: null, status: "unavailable" };
var SharePeopleContext = React2.createContext(EMPTY);
function SharePeopleProvider({ value, children }) {
  return /* @__PURE__ */ jsx(SharePeopleContext.Provider, { value, children });
}
function useSharePeople() {
  const people = React2.useContext(SharePeopleContext);
  const { request } = people;
  React2.useEffect(() => {
    request?.();
  }, [request]);
  return people;
}
function pickableUsers(people) {
  const me = people.currentUser;
  const others = people.users.filter((u) => u.id !== me?.id);
  return me ? [{ ...me, label: `${me.label} (me)` }, ...others] : others;
}

// src/ui/shareAudience.ts
var MAX_NAMED = 3;
function audienceIds(link) {
  const target = link.target;
  if (target === "team") return [...link.targetTeamIds ?? []];
  if (target === "user") return [...link.targetUserIds ?? []];
  return null;
}
function shareAudienceLabel(link, directory) {
  const ids = audienceIds(link);
  if (ids === null) return null;
  if (ids.length === 0) return "Nobody";
  const names = new Map((directory ?? []).map((option) => [option.id, option.label]));
  for (const ref of (link.target === "team" ? link.targetTeams : link.targetUsers) ?? []) {
    if (ref.name) names.set(ref.id, ref.name);
  }
  const named = ids.map((id) => names.get(id) ?? id);
  if (named.length <= MAX_NAMED) return named.join(", ");
  return `${named.slice(0, MAX_NAMED).join(", ")} +${named.length - MAX_NAMED} more`;
}
var EXPIRY_OPTIONS = [
  { label: "Never", days: null },
  { label: "1 day", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 }
];
function linkTerms(link, now) {
  const terms = [];
  if (link.expiresAt == null) {
    terms.push({ label: "Never expires", expired: false });
  } else {
    const expired = link.expiresAt <= now;
    terms.push({
      label: expired ? `Expired ${new Date(link.expiresAt).toLocaleDateString()}` : `Expires ${new Date(link.expiresAt).toLocaleDateString()}`,
      expired
    });
  }
  if (link.maxViews != null && link.maxViews > 0) {
    const spent = link.viewCount >= link.maxViews;
    terms.push({
      label: spent ? `Open limit reached (${link.viewCount}/${link.maxViews})` : `${link.viewCount}/${link.maxViews} opens used`,
      expired: spent
    });
  }
  return terms;
}
function fullUrl(link, origin) {
  if (link.shareUrl && /^https?:\/\//.test(link.shareUrl)) return link.shareUrl;
  if (!link.shareUrl) return null;
  const base = origin ?? (typeof location !== "undefined" ? location.origin : "");
  if (!base) return null;
  return `${base}${link.shareUrl.startsWith("/") ? "" : "/"}${link.shareUrl}`;
}
var PICKER_SEARCH_THRESHOLD = 8;
function PrincipalPicker({
  label,
  options,
  selected,
  onToggle,
  status,
  noun,
  onRetry
}) {
  const [query, setQuery] = React2.useState("");
  const q = query.trim().toLowerCase();
  const repeated = React2.useMemo(() => {
    const counts = /* @__PURE__ */ new Map();
    for (const o of options) counts.set(o.label, (counts.get(o.label) ?? 0) + 1);
    return counts;
  }, [options]);
  const shown = q ? options.filter((o) => selected.includes(o.id) || `${o.label} ${o.detail ?? ""}`.toLowerCase().includes(q)) : options;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx(Label, { children: label }),
    options.length > PICKER_SEARCH_THRESHOLD ? /* @__PURE__ */ jsx(
      Input,
      {
        value: query,
        placeholder: noun === "people" ? "Search people by name or email" : "Search teams",
        "aria-label": noun === "people" ? "Search people" : "Search teams",
        onChange: (event) => setQuery(event.target.value)
      }
    ) : null,
    shown.length ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1.5", role: "group", "aria-label": label, children: shown.map((option) => {
      const active = selected.includes(option.id);
      return /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-pressed": active,
          title: option.detail,
          onClick: () => onToggle(option.id),
          children: /* @__PURE__ */ jsxs(Badge, { variant: active ? void 0 : "outline", className: "gap-1", children: [
            active ? /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\u2713" }) : null,
            option.label,
            option.detail && (repeated.get(option.label) ?? 0) > 1 ? /* @__PURE__ */ jsxs("span", { className: "font-normal opacity-70", children: [
              "\xB7 ",
              option.detail
            ] }) : null
          ] })
        },
        option.id
      );
    }) }) : q ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Nobody matches \u201C",
      query.trim(),
      "\u201D."
    ] }) : null,
    status === "loading" ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Loading ",
      noun,
      "\u2026"
    ] }) : status === "unavailable" ? /* @__PURE__ */ jsxs("p", { className: "flex flex-wrap items-center gap-2 text-xs text-destructive", children: [
      noun === "people" ? "The member list couldn't be loaded, so only you can be chosen right now." : "Your teams couldn't be loaded \u2014 that is a failed read, not an empty list.",
      onRetry ? /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", onClick: onRetry, children: "Retry" }) : null
    ] }) : !options.length ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: noun === "people" ? "Nobody else is in this organization yet." : "You aren't in any team yet." }) : null
  ] });
}
function ShareViewDialogContent({
  source,
  viewId,
  shareOrigin,
  userOptions,
  teamOptions,
  allowDynamic = true,
  servingNote,
  servingLabel,
  title = "Share view",
  onDone
}) {
  const { links, loading, error, create, update, revoke, refresh, supported } = useShareLinks({
    source,
    viewId,
    autoLoad: true
  });
  const [showRevoked, setShowRevoked] = React2.useState(false);
  const [copyFailedFor, setCopyFailedFor] = React2.useState(null);
  const now = useNow(3e4);
  const offeredLadder = useOfferedRungs();
  const [target, setTarget] = React2.useState("public");
  const [dataMode, setDataMode] = React2.useState("static");
  const [catalogResolution, setCatalogResolution] = React2.useState(DEFAULT_CATALOG_RESOLUTION);
  const [password, setPassword] = React2.useState("");
  const [expiryDays, setExpiryDays] = React2.useState(null);
  const [maxViews, setMaxViews] = React2.useState("");
  const [userIds, setUserIds] = React2.useState([]);
  const [teamIds, setTeamIds] = React2.useState([]);
  const [busy, setBusy] = React2.useState(false);
  const people = useSharePeople();
  const users = React2.useMemo(() => userOptions ?? pickableUsers(people), [userOptions, people]);
  const teams = teamOptions ?? people.teams;
  const accessRevision = useResourceRevision(SAVED_VIEW_ACCESS_RESOURCE);
  const firstRevision = React2.useRef(accessRevision);
  React2.useEffect(() => {
    if (accessRevision !== firstRevision.current) refresh();
  }, [accessRevision]);
  const external = isExternalTarget(target);
  const needsUsers = target === "user";
  const audienceHintId = React2.useId();
  const needsTeams = target === "team";
  const audience = needsUsers ? userIds : needsTeams ? teamIds : [];
  const audienceMissing = (needsUsers || needsTeams) && audience.length === 0;
  const audienceLabel = (link) => shareAudienceLabel(link, link.target === "user" ? users : teams);
  const toggleId = (id, ids, setIds) => {
    setIds(ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]);
  };
  const mint = async () => {
    if (busy || !viewId || audienceMissing) return;
    const resolvedUserIds = needsUsers ? audience : void 0;
    const resolvedTeamIds = needsTeams ? audience : void 0;
    setBusy(true);
    try {
      const expiresAt = expiryDays == null ? null : Date.now() + expiryDays * 864e5;
      const parsedMax = Number.parseInt(maxViews, 10);
      const resolvedDataMode = external || servingNote ? "static" : dataMode;
      const created = await create({
        target,
        dataMode: resolvedDataMode,
        catalogResolution: servingNote ? DEFAULT_CATALOG_RESOLUTION : catalogResolution,
        password: password.trim() || void 0,
        expiresAt,
        maxViews: Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null,
        targetUserIds: resolvedUserIds,
        targetTeamIds: resolvedTeamIds
      });
      if (created) {
        setPassword("");
        setMaxViews("");
      }
    } finally {
      setBusy(false);
    }
  };
  const copy = async (link) => {
    const url = fullUrl(link, shareOrigin);
    if (!url) return;
    try {
      if (!await writeClipboardText(url)) throw new Error("clipboard unavailable");
      notifySuccess("Link copied to clipboard");
    } catch {
      setCopyFailedFor(link.token);
      notifyWarning(
        "Couldn\u2019t copy the link",
        "Your browser blocked clipboard access. The full link is shown below \u2014 select and copy it."
      );
    }
  };
  const restore = async (link) => {
    await update(link.token, { revoked: false });
  };
  const visibleLinks = showRevoked ? links : links.filter((l) => !l.revoked);
  const revokedCount = links.length - links.filter((l) => !l.revoked).length;
  const linksOutcome = listOutcome({
    count: links.length,
    loading,
    failed: error !== void 0
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: title }),
      /* @__PURE__ */ jsx(DialogDescription, { children: "Create a link to share this view. Public links open without sign-in; add a password or an expiry for tighter control." })
    ] }),
    !supported ? /* @__PURE__ */ jsx("p", { className: "py-4 text-sm text-muted-foreground", children: "Sharing isn't available for this view source." }) : /* @__PURE__ */ jsxs("div", { className: "space-y-4 py-2 min-w-0 w-full", children: [
      /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
        /* @__PURE__ */ jsx(
          Field,
          {
            label: "Audience",
            hint: external ? "External \xB7 anyone with the link, no sign-in (opens on the public page)." : "Internal \xB7 the audience must sign in (opens on the private page).",
            ownsControl: true,
            children: /* @__PURE__ */ jsxs(
              Select,
              {
                value: target,
                onValueChange: (value) => {
                  const nextTarget = value;
                  setTarget(nextTarget);
                  if (isExternalTarget(nextTarget)) setDataMode("static");
                },
                children: [
                  /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
                  /* @__PURE__ */ jsx(SelectContent, { children: offeredOptions(SHARE_TARGETS, offeredLadder, (o) => SHARE_TARGET_RUNG[o]).map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option, children: SHARE_TARGET_LABELS[option] }, option)) })
                ]
              }
            )
          }
        ),
        servingNote ? /* @__PURE__ */ jsx(Field, { label: "Data", children: /* @__PURE__ */ jsx("p", { className: "rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground", children: servingNote }) }) : /* @__PURE__ */ jsx(
          Field,
          {
            label: "Data",
            hint: external ? "Public links use static snapshots frozen at share time." : allowDynamic ? "Choose whether viewers see a frozen snapshot or live queries." : "Only static snapshots are available for this view.",
            ownsControl: true,
            children: /* @__PURE__ */ jsxs(
              Select,
              {
                value: external ? "static" : dataMode,
                onValueChange: (value) => setDataMode(value),
                disabled: !allowDynamic || external,
                children: [
                  /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
                  /* @__PURE__ */ jsx(SelectContent, { children: VIEW_DATA_MODES.map((option) => /* @__PURE__ */ jsx(
                    SelectItem,
                    {
                      value: option,
                      disabled: option === "dynamic" && (!allowDynamic || external),
                      children: VIEW_DATA_MODE_LABELS[option]
                    },
                    option
                  )) })
                ]
              }
            )
          }
        ),
        servingNote ? null : /* @__PURE__ */ jsx(
          Field,
          {
            label: "Catalog overlay",
            hint: (
              // The same sentence for every audience, because the fact it
              // states is about the reading SURFACE, not the reader: a
              // share link opens in a standalone viewer page, which has no
              // catalog of its own for anyone.
              catalogResolutionDeliversOverlay(catalogResolution) ? "Whose tags, labels and risk the reader is shown \u2014 yours, frozen as they are now." : "Share pages have no catalog of their own, so this link will show no labels, tags or risk at all."
            ),
            ownsControl: true,
            children: /* @__PURE__ */ jsxs(
              Select,
              {
                value: catalogResolution,
                onValueChange: (value) => setCatalogResolution(value),
                children: [
                  /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
                  /* @__PURE__ */ jsx(SelectContent, { children: CATALOG_RESOLUTIONS.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option, children: CATALOG_RESOLUTION_LABELS[option] }, option)) })
                ]
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(Field, { label: "Expires", ownsControl: true, children: /* @__PURE__ */ jsxs(
          Select,
          {
            value: String(expiryDays ?? "null"),
            onValueChange: (value) => setExpiryDays(value === "null" ? null : Number(value)),
            children: [
              /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
              /* @__PURE__ */ jsx(SelectContent, { children: EXPIRY_OPTIONS.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: String(option.days ?? "null"), children: option.label }, option.label)) })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Password (optional)", children: /* @__PURE__ */ jsx(
          Input,
          {
            type: "password",
            value: password,
            placeholder: "No password",
            onChange: (event) => setPassword(event.target.value)
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Max opens (optional)", children: /* @__PURE__ */ jsx(
          Input,
          {
            inputMode: "numeric",
            value: maxViews,
            placeholder: "Unlimited",
            onChange: (event) => setMaxViews(event.target.value.replace(/[^0-9]/g, ""))
          }
        ) })
      ] }),
      needsUsers && /* @__PURE__ */ jsx(
        PrincipalPicker,
        {
          label: "People",
          options: users,
          selected: userIds,
          onToggle: (id) => toggleId(id, userIds, setUserIds),
          status: people.status,
          noun: "people"
        }
      ),
      needsTeams && /* @__PURE__ */ jsx(
        PrincipalPicker,
        {
          label: "Teams",
          options: teams,
          selected: teamIds,
          onToggle: (id) => toggleId(id, teamIds, setTeamIds),
          status: teamOptions ? "ready" : people.teamsStatus ?? "ready",
          noun: "teams",
          ...people.retryTeams ? { onRetry: people.retryTeams } : {}
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: () => void mint(),
            disabled: busy || !viewId || audienceMissing,
            "aria-describedby": audienceMissing ? audienceHintId : void 0,
            children: "Create link"
          }
        ),
        audienceMissing ? /* @__PURE__ */ jsx("p", { id: audienceHintId, className: "text-xs text-muted-foreground", children: needsUsers ? "Pick at least one person \u2014 a link targeted at nobody can't be opened by anybody." : "Pick at least one team \u2014 a link targeted at no team can't be opened by anybody." }) : null
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-2 min-w-0 w-full", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsx(Label, { children: "Existing links" }),
          revokedCount > 0 && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "text-xs text-muted-foreground underline",
              onClick: () => setShowRevoked((value) => !value),
              children: showRevoked ? "Hide revoked" : `Show ${revokedCount} revoked`
            }
          )
        ] }),
        linksOutcome === "ready" && error ? (
          // Rows win over a failed read — blanking a list the owner is
          // looking at would be worse, and is the shared rule — but a
          // list that is out of date is not a list that is current, and
          // on an access-audit surface that difference matters.
          /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: "This list may be out of date \u2014 the last refresh failed." })
        ) : null,
        linksOutcome === "loading" ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Loading\u2026" }) : linksOutcome === "failed" ? (
          // NOT "No share links yet". An owner auditing who has access to
          // a case saw that copy for a request that failed, concluded
          // nothing was shared, and minted a second public link — with no
          // way to revoke the live ones, which they now did not know
          // about. `useShareLinks` has always computed `error`; nothing
          // read it.
          /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: "Couldn't load this view's share links. They may still exist \u2014 don't create another until this list loads." }),
            error ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground break-words", children: error }) : null,
            /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", onClick: () => refresh(), children: "Try again" })
          ] })
        ) : visibleLinks.length === 0 ? (
          // Either the view has no links at all, or every one it has is
          // revoked and hidden — which is a filter, so it says so.
          /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: revokedCount > 0 ? `No active share links (${revokedCount} revoked).` : "No share links yet." })
        ) : /* @__PURE__ */ jsx("ul", { className: "space-y-2 min-w-0 w-full", children: visibleLinks.map((link) => {
          const url = fullUrl(link, shareOrigin);
          return /* @__PURE__ */ jsxs(
            "li",
            {
              className: "flex items-center justify-between gap-2 rounded-md border p-2 text-sm min-w-0 w-full",
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0 space-y-1", children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [
                    /* @__PURE__ */ jsx(Badge, { variant: link.revoked ? "outline" : void 0, children: SHARE_TARGET_LABELS[link.target] }),
                    audienceLabel(link) ? /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "max-w-[16rem] truncate", children: audienceLabel(link) }) : null,
                    /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Read-only" }),
                    link.hasPassword && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Password" }),
                    servingNote ? servingLabel ? /* @__PURE__ */ jsx(Badge, { variant: "outline", children: servingLabel }) : null : link.dataMode === "static" ? /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Snapshot" }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Live data" }),
                    link.revoked && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Revoked" }),
                    linkTerms(link, now).map((term) => /* @__PURE__ */ jsx(
                      Badge,
                      {
                        variant: "outline",
                        className: term.expired ? "text-destructive" : void 0,
                        children: term.label
                      },
                      term.label
                    ))
                  ] }),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-xs text-muted-foreground min-w-0 w-full", children: [
                    /* @__PURE__ */ jsx("span", { className: "flex-1 min-w-0 truncate block font-mono text-2xs", title: url ?? void 0, children: url ?? /* @__PURE__ */ jsx("span", { className: "text-destructive", children: link.shareUrlUnavailable ?? "No viewer URL is configured for this audience yet." }) }),
                    /* @__PURE__ */ jsxs(
                      "span",
                      {
                        className: "shrink-0 whitespace-nowrap",
                        ...link.lastOpenedAt ? { title: `Last opened ${new Date(link.lastOpenedAt).toLocaleString()}` } : {},
                        children: [
                          "\xB7 ",
                          link.viewCount,
                          " open",
                          link.viewCount === 1 ? "" : "s",
                          link.lastOpenedAt ? `, last ${formatRelativeTime(link.lastOpenedAt, { unit: "milliseconds", nowMs: now })}` : ""
                        ]
                      }
                    )
                  ] }),
                  copyFailedFor === link.token && url ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
                    "Not copied \u2014 your browser blocked the clipboard. Copy it by hand:",
                    /* @__PURE__ */ jsx("span", { className: "mt-1 block select-all break-all font-mono text-2xs text-foreground", children: url })
                  ] }) : null
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      variant: "outline",
                      size: "sm",
                      disabled: !url,
                      onClick: () => void copy(link),
                      children: "Copy"
                    }
                  ),
                  link.revoked ? /* @__PURE__ */ jsx(
                    Button,
                    {
                      variant: "outline",
                      size: "sm",
                      onClick: () => void restore(link),
                      children: "Restore"
                    }
                  ) : /* @__PURE__ */ jsx(
                    Button,
                    {
                      variant: "outline",
                      size: "sm",
                      onClick: () => void revoke(link.token),
                      children: "Revoke"
                    }
                  )
                ] })
              ]
            },
            link.token
          );
        }) }),
        viewId && linksOutcome === "ready" && links.length > 0 ? /* @__PURE__ */ jsx(
          ShareOpensPanel,
          {
            source,
            viewId,
            links,
            userLabel: (id) => users.find((o) => o.id === id)?.label ?? null
          }
        ) : null
      ] })
    ] }),
    /* @__PURE__ */ jsx(DialogFooter, { children: /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: onDone, children: "Done" }) })
  ] });
}
function ShareViewDialog({
  open,
  onOpenChange,
  source,
  viewId,
  shareOrigin,
  userOptions,
  teamOptions,
  allowDynamic = true,
  servingNote,
  servingLabel,
  title
}) {
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsx(DialogContent, { className: "sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden", children: open && /* @__PURE__ */ jsx(
    ShareViewDialogContent,
    {
      source,
      viewId,
      shareOrigin,
      userOptions,
      teamOptions,
      allowDynamic,
      ...servingNote ? { servingNote } : {},
      ...servingLabel ? { servingLabel } : {},
      ...title ? { title } : {},
      onDone: () => onOpenChange(false)
    },
    String(open)
  ) }) });
}
function phrase(warning) {
  if (warning.message) return warning.message;
  const count = warning.hiddenCount;
  const noun = warning.resourceType || "item";
  const plural = count === 1 ? noun : `${noun}s`;
  return `${count} ${plural} in this view ${count === 1 ? "is" : "are"} not visible to you.`;
}
function AccessWarningBanner({
  warnings,
  className
}) {
  if (!warnings?.length) return null;
  return /* @__PURE__ */ jsxs(Alert, { variant: "warning", className, children: [
    /* @__PURE__ */ jsx(AlertTitle, { children: "Some data is hidden" }),
    /* @__PURE__ */ jsx(AlertDescription, { children: /* @__PURE__ */ jsx("ul", { className: "list-disc space-y-1 pl-4", children: warnings.map((warning, index) => /* @__PURE__ */ jsx("li", { children: phrase(warning) }, `${warning.code}-${index}`)) }) })
  ] });
}
var DEFAULT_FILTER_CODEC = {
  toRules: (filters) => {
    const rules = [];
    for (const filter of filters) {
      if (Array.isArray(filter.value)) {
        const values = filter.value.map(String).filter(Boolean);
        if (values.length) rules.push({ field: filter.id, op: "in", value: values });
      } else if (typeof filter.value === "string" && filter.value !== "") {
        rules.push({ field: filter.id, op: "contains", value: filter.value });
      }
    }
    return rules;
  },
  fromRules: (rules) => {
    const filters = [];
    for (const rule of rules ?? []) {
      if (rule.op === "in") {
        const values = Array.isArray(rule.value) ? rule.value.map(String).filter(Boolean) : [];
        if (values.length) filters.push({ id: rule.field, value: values });
      } else if (rule.op === "contains" && rule.value != null && rule.value !== "") {
        filters.push({ id: rule.field, value: String(rule.value) });
      }
    }
    return filters;
  }
};
function visibilityToColumnPrefs(visibility, columnIds, columnOrder = []) {
  const known = new Set(columnIds);
  const named = columnOrder.filter((id) => known.has(id));
  const seen = new Set(named);
  const ordered = [...named, ...columnIds.filter((id) => !seen.has(id))];
  return ordered.map((id) => ({ id, visible: visibility[id] !== false }));
}
function columnPrefsToVisibility(prefs) {
  const visibility = {};
  for (const pref of prefs ?? []) visibility[pref.id] = pref.visible !== false;
  return visibility;
}
function columnPrefsToOrder(prefs) {
  return (prefs ?? []).map((pref) => pref.id);
}
function sortingToSortRules(sorting) {
  return sorting.map((s) => ({ field: s.id, direction: s.desc ? "desc" : "asc" }));
}
function sortRulesToSorting(rules) {
  return (rules ?? []).map((r) => ({ id: r.field, desc: r.direction === "desc" }));
}
function useTableViews(options) {
  const {
    source,
    tableId,
    identity,
    columnIds,
    filterCodec = DEFAULT_FILTER_CODEC,
    autoLoad,
    defaultDensity = "comfortable"
  } = options;
  const [columnVisibility, setColumnVisibility] = React2.useState({});
  const [columnOrder, setColumnOrder] = React2.useState([]);
  const [columnFilters, setColumnFilters] = React2.useState([]);
  const [sorting, setSorting] = React2.useState([]);
  const [density, setDensity] = React2.useState(defaultDensity);
  const codecRef = React2.useRef(filterCodec);
  React2.useEffect(() => {
    codecRef.current = filterCodec;
  });
  const views = useSavedViews({
    source,
    tableId,
    identity,
    ...autoLoad !== void 0 ? { autoLoad } : {},
    onApply: (state) => {
      setColumnVisibility(columnPrefsToVisibility(state.columns));
      setColumnOrder(columnPrefsToOrder(state.columns));
      setColumnFilters(codecRef.current.fromRules(state.filters));
      setSorting(sortRulesToSorting(state.sort));
      setDensity(state.density ?? defaultDensity);
    }
  });
  const onColumnVisibilityChange = React2.useCallback(
    (updater) => setColumnVisibility((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onColumnOrderChange = React2.useCallback(
    (updater) => setColumnOrder((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onColumnFiltersChange = React2.useCallback(
    (updater) => setColumnFilters((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onSortingChange = React2.useCallback(
    (updater) => setSorting((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const captureState = React2.useCallback(
    () => ({
      columns: visibilityToColumnPrefs(columnVisibility, columnIds, columnOrder),
      filters: filterCodec.toRules(columnFilters),
      sort: sortingToSortRules(sorting),
      density
    }),
    [columnVisibility, columnIds, columnOrder, filterCodec, columnFilters, sorting, density]
  );
  const saveCurrent = React2.useCallback(
    (name, scope) => views.save(name, captureState(), scope),
    [views, captureState]
  );
  const serverFilters = React2.useMemo(
    () => filterCodec.toRules(columnFilters),
    [filterCodec, columnFilters]
  );
  return {
    views,
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    density,
    setDensity,
    serverFilters,
    captureState,
    saveCurrent
  };
}
var INERT_SOURCE = {
  list: async () => [],
  create: async (view) => view,
  update: async (view) => view,
  remove: async () => {
  },
  setDefault: async () => {
  }
};
function useTableSavedViews(options) {
  const {
    enabled = false,
    tableId,
    columnIds,
    filterCodec,
    canShare,
    canSave,
    defaultDensity
  } = options;
  const context = useOptionalSavedViews();
  const active = enabled && context != null;
  const [saveOpen, setSaveOpen] = React2.useState(false);
  const [manageOpen, setManageOpen] = React2.useState(false);
  const granted = context?.permissions;
  const maySave = active && (granted?.canSave ?? false) && (canSave ?? true);
  const mayShare = active && (granted?.canShare ?? false) && (canShare ?? true);
  const mayDeleteShared = active && (granted?.canDeleteShared ?? false) && (canShare ?? true);
  const views = useTableViews({
    source: context?.source ?? INERT_SOURCE,
    tableId,
    identity: context?.identity ?? {},
    columnIds,
    autoLoad: active,
    ...filterCodec ? { filterCodec } : {},
    ...defaultDensity ? { defaultDensity } : {}
  });
  const exportContext = {
    tableId,
    ...views.views.activeViewId ? { viewId: views.views.activeViewId } : {},
    ...views.serverFilters.length > 0 ? { filters: { rules: views.serverFilters } } : {}
  };
  if (!active) {
    return { active: false, controlled: {}, toolbar: null, density: void 0, exportContext };
  }
  return {
    active: true,
    controlled: {
      columnVisibility: views.columnVisibility,
      onColumnVisibilityChange: views.onColumnVisibilityChange,
      columnOrder: views.columnOrder,
      onColumnOrderChange: views.onColumnOrderChange,
      columnFilters: views.columnFilters,
      onColumnFiltersChange: views.onColumnFiltersChange,
      sorting: views.sorting,
      onSortingChange: views.onSortingChange
    },
    density: views.density,
    exportContext,
    toolbar: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        SavedViewsMenu,
        {
          controller: views.views,
          canSave: maySave,
          onSaveCurrent: () => setSaveOpen(true),
          onManage: () => setManageOpen(true)
        }
      ),
      /* @__PURE__ */ jsx(
        ManageSavedViewsDialog,
        {
          open: manageOpen,
          onOpenChange: setManageOpen,
          controller: views.views,
          canManagePersonal: maySave,
          canManageShared: mayShare,
          canDeleteShared: mayDeleteShared
        }
      ),
      /* @__PURE__ */ jsx(
        SaveCurrentViewDialog,
        {
          open: saveOpen,
          onOpenChange: setSaveOpen,
          canShare: mayShare,
          onSave: async (name, scope) => {
            await views.saveCurrent(name, scope);
          }
        }
      )
    ] })
  };
}
var CONTRIBUTION_KINDS = ["address", "transaction", "catalog", "money_flow"];
var DEFAULT_CONTRIBUTION_POLICY = {
  enabled: true,
  admin_user_ids: [],
  allowed_kinds: [...CONTRIBUTION_KINDS],
  hourly_limit: 10,
  allow_self_review: false
};
function createContributionsSource(client) {
  const base = "/investigations";
  const publication = (token) => `${base}/publications/${encodeURIComponent(token)}/contributions`;
  const queue = (id) => `${base}/${encodeURIComponent(id)}/contributions`;
  const headers = (password) => password ? { "X-Share-Password": password } : void 0;
  return {
    async submit(token, requestId, draft, password) {
      return unwrapData(await client.post(
        publication(token),
        { request_id: requestId, draft },
        { headers: headers(password) }
      ));
    },
    async own(token, id, password) {
      return unwrapData(await client.get(
        `${publication(token)}/${encodeURIComponent(id)}`,
        { headers: headers(password), cacheTtlMs: 0 }
      ));
    },
    async list(caseId, status, cursor) {
      return unwrapData(await client.get(
        queue(caseId),
        { query: { status, limit: "30", ...cursor ? { cursor } : {} }, cacheTtlMs: 0 }
      ));
    },
    async decide(caseId, item, action, notes, draft) {
      return unwrapData(await client.post(
        `${queue(caseId)}/${encodeURIComponent(item.id)}`,
        { revision: item.revision, action, notes, draft }
      ));
    }
  };
}
var CONTRIBUTION_LABELS = {
  address: "Add an address",
  transaction: "Add a transaction",
  catalog: "Correct catalog information",
  money_flow: "Correct a money flow"
};
function ContributionFields({ value, onChange, allowedKinds = CONTRIBUTION_KINDS, disabled = false }) {
  const id = React2.useId();
  const update = (patch) => onChange({ ...value, ...patch });
  return /* @__PURE__ */ jsxs("fieldset", { disabled, className: "space-y-4", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-kind`, children: "Suggestion type" }),
      /* @__PURE__ */ jsx(
        NativeSelect,
        {
          id: `${id}-kind`,
          value: value.kind,
          onChange: (e) => update({ kind: e.target.value }),
          children: allowedKinds.map((kind) => /* @__PURE__ */ jsx("option", { value: kind, children: CONTRIBUTION_LABELS[kind] }, kind))
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-title`, children: "Summary" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          id: `${id}-title`,
          required: true,
          minLength: 3,
          maxLength: 200,
          value: value.title,
          onChange: (e) => update({ title: e.target.value })
        }
      )
    ] }),
    (value.kind === "address" || value.kind === "transaction") && /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-chain`, children: "Chain" }),
      /* @__PURE__ */ jsx(ChainSelect, { id: `${id}-chain`, valueKind: "caip2", value: value.chain_id ?? "", onChange: (chain) => update({ chain_id: chain }) })
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-ref`, children: value.kind === "address" ? "Address" : value.kind === "transaction" ? "Transaction hash" : "Catalog item or flow reference" }),
      /* @__PURE__ */ jsx(Input, { id: `${id}-ref`, required: true, maxLength: 256, value: value.reference, onChange: (e) => update({ reference: e.target.value }) })
    ] }),
    (value.kind === "catalog" || value.kind === "money_flow") && /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-value`, children: "Proposed correction" }),
      /* @__PURE__ */ jsx(Textarea, { id: `${id}-value`, required: true, maxLength: 2e3, value: value.proposed_value ?? "", onChange: (e) => update({ proposed_value: e.target.value }) }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "If accepted, this becomes a cited case finding. Platform catalog changes use a separate publication review." })
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-reason`, children: "Reason and supporting evidence" }),
      /* @__PURE__ */ jsx(
        Textarea,
        {
          id: `${id}-reason`,
          required: true,
          minLength: 10,
          maxLength: 4e3,
          value: value.rationale,
          onChange: (e) => update({ rationale: e.target.value })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Label, { htmlFor: `${id}-sources`, children: "Source links (one HTTPS URL per line, up to five)" }),
      /* @__PURE__ */ jsx(
        Textarea,
        {
          id: `${id}-sources`,
          value: value.evidence_urls.join("\n"),
          onChange: (e) => update({ evidence_urls: e.target.value.split("\n") })
        }
      ),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Only include evidence you have permission to publish. Do not include private keys or personal contact details." })
    ] })
  ] });
}

export { AccessWarningBanner, CATALOG_RESOLUTIONS, CATALOG_RESOLUTION_LABELS, CONTRIBUTION_KINDS, CONTRIBUTION_LABELS, ContributionFields, DEFAULT_CATALOG_RESOLUTION, DEFAULT_CONTRIBUTION_POLICY, DEFAULT_FILTER_CODEC, GRAPHS_TABLE_ID, ManageSavedViewsDialog, SAVED_VIEWS_PACKAGE, SAVED_VIEW_ACCESS_RESOURCE, SCOPE_LABELS, SHARE_DEMO_ROUTE, SHARE_ERROR_CODES, SHARE_TARGETS, SHARE_TARGET_LABELS, SHARE_TARGET_RUNG, SV_ACTIVE_SUFFIX, SV_DRAFT_SUFFIX, SV_MAX_NAME_LENGTH, SV_MAX_SHARE_URL_LENGTH, SV_OPENS_REFRESH_MS, SV_URL_PARAM, SaveCurrentViewDialog, SavedViewsList, SavedViewsMenu, SavedViewsProvider, ShareOpensPanel, SharePeopleProvider, ShareViewDialog, VIEW_DATA_MODES, VIEW_DATA_MODE_LABELS, VIEW_SCOPES, applyViewStateToUrl, audienceIds, catalogResolutionDeliversOverlay, columnPrefsToVisibility, createApiSavedViewsSource, createContributionsSource, createInMemorySavedViewsSource, decodeViewState, describeOpener, describeOpens, describeShareSubtitle, describeSnapshotGap, describeVia, emptyView, encodeViewState, isEmptyState, isExternalTarget, isViewScope, linkTerms, newViewId, payloadNeedsCredentials, pickableUsers, readShareRouteFromUrl, readViewStateFromUrl, resolvedShareFromWire, shareAudienceLabel, shareErrorCodeOf, sortRulesToSorting, sortingToSortRules, useGraphViews, useOptionalSavedViews, useResolveShare, useSavedViews, useShareLinks, useSharePeople, useTableSavedViews, useTableViews, visibilityToColumnPrefs };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map